import React, { useContext, useMemo } from "react";
import { Box, Chip, Grid, Stack, Typography } from "@mui/material";
import { useParams } from "react-router-dom";
import { useQuery } from "react-query";

import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import PersonOutlineRoundedIcon from "@mui/icons-material/PersonOutlineRounded";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import EuroOutlinedIcon from "@mui/icons-material/EuroOutlined";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";

import PageHeader from "../../../components/PageHeader";
import SectionCard from "../../../components/SectionCard";
import DataTable from "../../../components/DataTable";
import Spinner from "../../../components/Spinner";
import { getOrderInfo } from "../../../api/orderApi";
import { LanguageContext } from "../../../components/LanguageContext";

const StatTile = ({ icon, label, value, sub }) => (
    <SectionCard>
        <Stack direction="row" spacing={2} alignItems="flex-start">
            <Box
                sx={(theme) => ({
                    width: 40,
                    height: 40,
                    borderRadius: 2,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    bgcolor:
                        theme.palette.mode === "dark"
                            ? "rgba(255,255,255,0.04)"
                            : "rgba(15,23,42,0.04)",
                    color: "primary.main",
                    flexShrink: 0,
                })}
            >
                {icon}
            </Box>
            <Box sx={{ minWidth: 0 }}>
                <Typography
                    variant="overline"
                    color="text.secondary"
                    sx={{ display: "block", lineHeight: 1.2 }}
                >
                    {label}
                </Typography>
                <Typography
                    variant="h6"
                    sx={{
                        fontWeight: 700,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                    }}
                >
                    {value}
                </Typography>
                {sub && (
                    <Typography variant="body2" color="text.secondary">
                        {sub}
                    </Typography>
                )}
            </Box>
        </Stack>
    </SectionCard>
);

const fmtDate = (d) => {
    if (!d) return "—";
    try {
        return new Date(d).toLocaleDateString("sr-RS");
    } catch {
        return d;
    }
};

const OrderInfoPage = () => {
    const { id } = useParams();
    const { translations } = useContext(LanguageContext) || {};

    const { data, isLoading } = useQuery({
        queryKey: ["getOrderInfo", id],
        queryFn: () => getOrderInfo(id),
        staleTime: 5 * 60 * 1000,
    });

    const order = data?.data?.order || null;

    const rows = useMemo(() => {
        if (!order?.order_items) return [];
        return order.order_items.map((item, index) => ({ id: index, ...item }));
    }, [order]);

    const columns = useMemo(
        () => [
            {
                field: "productCode",
                headerName:
                    translations?.poslovodjaAcquisitionPageColumnProductCode ||
                    "Šifra",
                minWidth: 130,
                renderCell: (p) => (
                    <Chip
                        size="small"
                        variant="outlined"
                        label={p.value}
                        sx={{ fontFamily: "monospace", fontWeight: 600 }}
                    />
                ),
            },
            {
                field: "productName",
                headerName: translations?.productName || "Naziv proizvoda",
                minWidth: 280,
                flex: 2,
                renderCell: (p) => (
                    <Typography
                        sx={{
                            fontWeight: 500,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                        }}
                        title={p.value}
                    >
                        {p.value}
                    </Typography>
                ),
            },
            {
                field: "quantity",
                headerName: translations?.quantity || "Količina",
                width: 110,
                align: "center",
                headerAlign: "center",
                renderCell: (p) => (
                    <Typography
                        sx={{ fontFamily: "monospace", fontWeight: 700 }}
                    >
                        {p.value}
                    </Typography>
                ),
            },
            {
                field: "orderPrice",
                headerName: translations?.price || "Cena",
                width: 130,
                align: "center",
                headerAlign: "center",
                renderCell: (p) => (
                    <Typography
                        sx={{
                            fontFamily: "monospace",
                            fontWeight: 600,
                            color: "success.main",
                        }}
                    >
                        € {parseFloat(p.value || 0).toFixed(2)}
                    </Typography>
                ),
            },
            {
                field: "orderRebate",
                headerName: translations?.rebate || "Rabat",
                width: 100,
                align: "center",
                headerAlign: "center",
                renderCell: (p) => (
                    <Typography
                        variant="body2"
                        sx={{
                            color: p.value > 0 ? "warning.main" : "text.secondary",
                            fontWeight: 600,
                        }}
                    >
                        {p.value}%
                    </Typography>
                ),
            },
            {
                field: "lastSupplyPrice",
                headerName: translations?.lastPrice || "Posl. cena",
                width: 130,
                align: "center",
                headerAlign: "center",
                renderCell: (p) => (
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ fontFamily: "monospace" }}
                    >
                        € {parseFloat(p.value || 0).toFixed(2)}
                    </Typography>
                ),
            },
            {
                field: "note",
                headerName: translations?.note || "Napomena",
                minWidth: 150,
                flex: 1,
                renderCell: (p) => (
                    <Typography
                        variant="body2"
                        sx={{
                            fontStyle: p.value ? "normal" : "italic",
                            color: p.value ? "text.primary" : "text.disabled",
                        }}
                    >
                        {p.value || translations?.noNote || "—"}
                    </Typography>
                ),
            },
        ],
        [translations]
    );

    if (isLoading) return <Spinner variant="inline" />;
    if (!order) return null;

    return (
        <Box>
            <PageHeader
                icon={<ReceiptLongOutlinedIcon />}
                eyebrow={translations?.orderInfo || "Narudžbenica"}
                title={`#${order.document_number || ""}`}
                description={`${translations?.warehouse || "Magacin"}: ${
                    order.warehouse_code
                }`}
            />

            <Stack spacing={3}>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={3}>
                        <StatTile
                            icon={<PersonOutlineRoundedIcon />}
                            label={translations?.user || "Korisnik"}
                            value={`${order.user_name || ""} ${
                                order.user_surname || ""
                            }`}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <StatTile
                            icon={<LocalShippingOutlinedIcon />}
                            label={translations?.supplier || "Dobavljač"}
                            value={order.supplier_name}
                            sub={`${
                                translations?.code || "Šifra"
                            }: ${order.supplier_code}`}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <StatTile
                            icon={<EuroOutlinedIcon />}
                            label={translations?.totalValue || "Vrednost"}
                            value={`€ ${(order.total_value || 0).toFixed(2)}`}
                            sub={order.currency_code || "EUR"}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <StatTile
                            icon={<Inventory2OutlinedIcon />}
                            label={translations?.totalItems || "Stavki"}
                            value={order.total_items || 0}
                            sub={translations?.products || "proizvoda"}
                        />
                    </Grid>
                </Grid>

                <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                        <SectionCard
                            title={translations?.dateInfo || "Datumi"}
                        >
                            <Stack spacing={2}>
                                <Stack
                                    direction="row"
                                    alignItems="center"
                                    spacing={1.5}
                                >
                                    <CalendarTodayOutlinedIcon
                                        sx={{ color: "text.secondary" }}
                                    />
                                    <Box>
                                        <Typography
                                            variant="overline"
                                            color="text.secondary"
                                            sx={{ display: "block" }}
                                        >
                                            {translations?.orderDate ||
                                                "Datum naručivanja"}
                                        </Typography>
                                        <Typography
                                            variant="body1"
                                            sx={{ fontWeight: 600 }}
                                        >
                                            {fmtDate(order.date)}
                                        </Typography>
                                    </Box>
                                </Stack>
                                <Stack
                                    direction="row"
                                    alignItems="center"
                                    spacing={1.5}
                                >
                                    <CalendarTodayOutlinedIcon
                                        sx={{ color: "text.secondary" }}
                                    />
                                    <Box>
                                        <Typography
                                            variant="overline"
                                            color="text.secondary"
                                            sx={{ display: "block" }}
                                        >
                                            {translations?.paymentDate ||
                                                "Datum plaćanja"}
                                        </Typography>
                                        <Typography
                                            variant="body1"
                                            sx={{ fontWeight: 600 }}
                                        >
                                            {fmtDate(order.payment_date)}
                                        </Typography>
                                    </Box>
                                </Stack>
                            </Stack>
                        </SectionCard>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <SectionCard
                            title={
                                translations?.deliveryInfo ||
                                "Dostava i magacin"
                            }
                        >
                            <Stack spacing={2}>
                                <Box>
                                    <Typography
                                        variant="overline"
                                        color="text.secondary"
                                        sx={{ display: "block" }}
                                    >
                                        {translations?.warehouse || "Magacin"}
                                    </Typography>
                                    <Typography
                                        variant="body1"
                                        sx={{ fontWeight: 600 }}
                                    >
                                        {order.warehouse_code}
                                    </Typography>
                                </Box>
                                <Box>
                                    <Typography
                                        variant="overline"
                                        color="text.secondary"
                                        sx={{ display: "block" }}
                                    >
                                        {translations?.storeCode || "Prodavnica"}
                                    </Typography>
                                    <Typography
                                        variant="body1"
                                        sx={{ fontWeight: 600 }}
                                    >
                                        {order.store_code}
                                    </Typography>
                                </Box>
                            </Stack>
                        </SectionCard>
                    </Grid>
                </Grid>

                <SectionCard
                    title={translations?.orderItems || "Stavke narudžbenice"}
                    description={
                        translations?.orderItemsSubtitle ||
                        "Pregled stavki sa cenama i količinama."
                    }
                    padded={false}
                >
                    <DataTable
                        rows={rows}
                        columns={columns}
                        loading={false}
                        rowHeight={56}
                        showToolbar={false}
                        emptyIcon={<Inventory2OutlinedIcon />}
                        emptyTitle={
                            translations?.noOrderItems || "Nema stavki"
                        }
                    />
                </SectionCard>
            </Stack>
        </Box>
    );
};

export default OrderInfoPage;
